import React, { useMemo } from 'react';
import { FileText, CheckCircle, AlertTriangle, XCircle, Star, Printer, RotateCcw, Trophy, Medal } from 'lucide-react';
import type { Interview } from './index';

interface InterviewReportProps {
  interview: Interview;
  onNewInterview: () => void;
}

const evaluationIcons = {
  not_met: XCircle,
  partially_met: AlertTriangle,
  fully_met: CheckCircle,
  exceeds: Star,
} as const;

const evaluationColors = {
  not_met: 'text-red-600 bg-red-50',
  partially_met: 'text-yellow-600 bg-yellow-50',
  fully_met: 'text-green-600 bg-green-50',
  exceeds: 'text-purple-600 bg-purple-50',
} as const;

const evaluationLabels = {
  not_met: 'Não atende',
  partially_met: 'Atende parcialmente',
  fully_met: 'Atende completamente',
  exceeds: 'Excede o requisito',
} as const;

const matchCategories = {
  gold: {
    icon: Trophy,
    label: 'Gold Match 🏆',
    description: '≥ 70% das avaliações são Excede ou Atende',
    color: 'text-yellow-600 bg-yellow-50 border-yellow-200',
  },
  silver: {
    icon: Medal,
    label: 'Silver Match 🥈',
    description: '≥ 50% das avaliações são Atende, sem mais de 20% em Não Atende',
    color: 'text-gray-600 bg-gray-50 border-gray-200',
  },
  bronze: {
    icon: Medal,
    label: 'Bronze Match 🥉',
    description: '≥ 30% das avaliações são Atende Parcialmente ou melhor',
    color: 'text-orange-600 bg-orange-50 border-orange-200',
  },
  none: {
    icon: XCircle,
    label: 'No Match ❌',
    description: '≥ 50% das avaliações são Não Atende',
    color: 'text-red-600 bg-red-50 border-red-200',
  },
} as const;

export const InterviewReport: React.FC<InterviewReportProps> = ({
  interview,
  onNewInterview,
}) => {
  const handlePrint = () => {
    window.print();
  };

  const matchCategory = useMemo(() => {
    const totalSkills = interview.skills.length;
    const evaluations = interview.skills.reduce((acc, skill) => {
      if (!skill.evaluation) return acc;
      acc[skill.evaluation] = (acc[skill.evaluation] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const notMetPercentage = (evaluations.not_met || 0) / totalSkills * 100;
    const meetsOrExceedsPercentage = ((evaluations.fully_met || 0) + (evaluations.exceeds || 0)) / totalSkills * 100;
    const meetsPercentage = (evaluations.fully_met || 0) / totalSkills * 100;
    const partialOrBetterPercentage = ((evaluations.partially_met || 0) + (evaluations.fully_met || 0) + (evaluations.exceeds || 0)) / totalSkills * 100;

    if (meetsOrExceedsPercentage >= 70) {
      return matchCategories.gold;
    }
    if (meetsPercentage >= 50 && notMetPercentage <= 20) {
      return matchCategories.silver;
    }
    if (partialOrBetterPercentage >= 30) {
      return matchCategories.bronze;
    }
    return matchCategories.none;
  }, [interview.skills]);

  return (
    <div className="bg-white rounded-lg shadow-md print:shadow-none">
      <div className="p-6 border-b flex items-center justify-between print:border-none print:pb-2">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2 print:text-xl">
            Relatório de Avaliação Técnica
          </h1>
          <div className="text-gray-600">
            <p className="font-medium">{interview.position}</p>
            <p>{interview.candidateName}</p>
            <div className="text-sm mt-2 space-y-1">
              <p>
                <span className="font-medium">Entrevistador:</span> {interview.interviewer}
              </p>
              <p>
                <span className="font-medium">Data da Entrevista:</span>{' '}
                {new Date(interview.interviewDate).toLocaleDateString('pt-BR', {
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric',
                })}
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 print:hidden">
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
          >
            <Printer className="w-4 h-4" />
            Imprimir
          </button>
          <button
            onClick={onNewInterview}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            Nova Avaliação
          </button>
        </div>
      </div>

      <div className="p-6 space-y-8 print:space-y-4">
        <div className={`flex items-center gap-3 p-4 rounded-lg border ${matchCategory.color}`}>
          <matchCategory.icon className="w-5 h-5" />
          <div>
            <p className="font-medium">{matchCategory.label}</p>
            <p className="text-sm opacity-90">{matchCategory.description}</p>
          </div>
        </div>

        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2 print:text-base">
            <FileText className="w-5 h-5 text-blue-600" />
            Avaliação de Skills
          </h2>

          <div className="space-y-6 print:space-y-4">
            {interview.skills.map((skill) => {
              if (!skill.evaluation) return null;
              const Icon = evaluationIcons[skill.evaluation];
              const colorClass = evaluationColors[skill.evaluation];
              const label = evaluationLabels[skill.evaluation];

              return (
                <div key={skill.id} className="border-b pb-6 last:border-0 last:pb-0 print:pb-4">
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div>
                      <h3 className="font-medium text-gray-900">
                        {skill.name}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {skill.description}
                      </p>
                    </div>
                    <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm ${colorClass}`}>
                      <Icon className="w-4 h-4" />
                      <span>{label}</span>
                    </div>
                  </div>

                  {skill.notes && (
                    <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-700">
                      {skill.notes}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="space-y-4 pt-6 border-t print:pt-4">
          <div>
            <h3 className="font-medium text-gray-900 mb-2">
              Considerações Finais
            </h3>
            <div className="bg-gray-50 rounded-lg p-4 text-gray-700">
              {interview.finalNotes}
            </div>
          </div>

          <div>
            <h3 className="font-medium text-gray-900 mb-2">
              Recomendação
            </h3>
            <div className={`rounded-lg p-4 ${
              interview.recommendation === 'approve'
                ? 'bg-green-50 text-green-700'
                : 'bg-red-50 text-red-700'
            }`}>
              <div className="flex items-center gap-2">
                {interview.recommendation === 'approve' ? (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">Continuar no Processo</span>
                  </>
                ) : (
                  <>
                    <XCircle className="w-5 h-5" />
                    <span className="font-medium">Encerrar Processo</span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};